import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowUpRight, Cog, Phone } from "lucide-react";
import Seo, { buildLocalBusinessJsonLd } from "@/components/Seo";
import EngineBackdrop from "@/components/EngineBackdrop";
import { Wordmark } from "@/components/layout/Header";
import { business, telHref, whatsappHref } from "@/business-config";
import "./Experience.css";

const stages = [
  {
    id: "acasa",
    title: "IMPERIAL CARS",
    side: "left",
    content: (
      <>
        <h1><span>Imperial Cars</span>{" "}<span>A&amp;F</span></h1>
        <p className="scene-lead">Diagnosticare, reparații mecanice și electrice, revizii. În Târgu Mureș.</p>
        <a className="scene-next" href="#servicii">
          Derulează pentru servicii <ArrowDown size={18} />
        </a>
      </>
    ),
  },
  {
    id: "servicii",
    title: "DIAGNOSTICARE",
    side: "right",
    content: (
      <>
        <h2><span>Diagnosticăm</span>{" "}<span>cauza.</span></h2>
        <p className="scene-lead">Citire erori, inspecție și măsurători electrice înainte de stabilirea reparației.</p>
        <p className="scene-detail">Diagnoză computerizată · Electricitate auto · Inspecție</p>
      </>
    ),
  },
  {
    id: "reparatii",
    title: "MECANICĂ",
    side: "left",
    content: (
      <>
        <h2><span>Reparăm</span>{" "}<span>mecanica.</span></h2>
        <p className="scene-lead">Intervenții la motor, transmisie, ambreiaj și sistemele auxiliare, în funcție de problema constatată.</p>
        <p className="scene-detail">Motor · Transmisie · Ambreiaj · Mecanică generală</p>
      </>
    ),
  },
  {
    id: "intretinere",
    title: "ÎNTREȚINERE",
    side: "right",
    content: (
      <>
        <h2><span>Verificăm</span>{" "}<span>rularea.</span></h2>
        <p className="scene-lead">Frânare, suspensie și direcție, împreună cu reviziile și consumabilele necesare.</p>
        <p className="scene-detail">Frâne · Suspensie · Direcție · Revizii periodice</p>
      </>
    ),
  },
  {
    id: "despre-service",
    title: "FĂRĂ OCOLIȘURI",
    side: "left",
    content: (
      <>
        <h2><span>Știi înainte</span>{" "}<span>să începem.</span></h2>
        <p className="scene-lead">Verificăm mașina, explicăm ce trebuie reparat și comunicăm costul. Lucrările suplimentare se fac după acordul tău.</p>
        <p className="scene-detail">Constatare · Deviz · Acord · Reparație</p>
      </>
    ),
  },
  {
    id: "contact",
    title: "PROGRAMĂRI",
    side: "right",
    content: (
      <>
        <h2><span>Programează</span>{" "}<span>mașina.</span></h2>
        <a className="scene-phone" href={telHref()}><Phone size={20} /> {business.phone.display}</a>
        <p className="scene-detail">{business.address.street} · {business.address.city}<br />{business.hours.display}</p>
        <div className="scene-actions">
          <a className="button" href={whatsappHref()} target="_blank" rel="noreferrer">
            Scrie pe WhatsApp <ArrowUpRight size={18} />
          </a>
          <a className="text-link" href={business.address.directionsUrl} target="_blank" rel="noreferrer">
            Deschide traseul <ArrowUpRight size={17} />
          </a>
        </div>
        <div className="scene-legal">
          <Link to="/politica-de-confidentialitate">Confidențialitate</Link>
          <Link to="/politica-de-cookies">Cookies</Link>
        </div>
      </>
    ),
  },
];

function ExperienceLoader({ progress, ready }) {
  return (
    <div
      className={`experience-loader${ready ? " is-complete" : ""}`}
      role="status"
      aria-live="polite"
      aria-hidden={ready}
    >
      <div className="loader-core">
        <div className="loader-cogs" aria-hidden="true">
          <Cog className="loader-cog loader-cog-primary" strokeWidth={1.25} />
          <Cog className="loader-cog loader-cog-accent" strokeWidth={1.5} />
        </div>
        <span className="loader-status">Se încarcă</span>
        <span className="loader-progress" aria-hidden="true">
          <i style={{ transform: `scaleX(${progress})` }} />
        </span>
      </div>
    </div>
  );
}

export default function Home() {
  const experience = useRef(null);
  const [activeStage, setActiveStage] = useState(0);
  const [experienceReady, setExperienceReady] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const loadingStartedAt = useRef(Date.now());
  const revealTimer = useRef(null);

  const handleExperienceReady = useCallback(() => {
    setLoadingProgress(1);
    clearTimeout(revealTimer.current);
    const remaining = Math.max(0, 650 - (Date.now() - loadingStartedAt.current));
    revealTimer.current = setTimeout(() => setExperienceReady(true), remaining);
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle("experience-loading", !experienceReady);
    return () => document.documentElement.classList.remove("experience-loading");
  }, [experienceReady]);

  useEffect(() => {
    const viewport = window.visualViewport;
    const experienceNode = experience.current;
    let frame = 0;
    const syncViewport = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const height = viewport?.height || window.innerHeight;
        experienceNode?.style.setProperty("--experience-visible-height", `${height}px`);
      });
    };
    syncViewport();
    viewport?.addEventListener("resize", syncViewport);
    viewport?.addEventListener("scroll", syncViewport);
    window.addEventListener("resize", syncViewport);
    return () => {
      cancelAnimationFrame(frame);
      viewport?.removeEventListener("resize", syncViewport);
      viewport?.removeEventListener("scroll", syncViewport);
      window.removeEventListener("resize", syncViewport);
      experienceNode?.style.removeProperty("--experience-visible-height");
    };
  }, []);

  useEffect(() => () => clearTimeout(revealTimer.current), []);

  return (
    <>
      <Seo path="/" jsonLd={buildLocalBusinessJsonLd()} />
      <div
        className={`service-experience ${experienceReady ? "is-ready" : "is-preparing"}`}
        ref={experience}
        data-stage={activeStage}
        aria-busy={!experienceReady}
      >
        <div className="experience-brand">
          <Link to="/" aria-label="Imperial Cars, pagina principală"><Wordmark /></Link>
          <span className="experience-location">Service auto · Târgu Mureș</span>
          <a className="experience-call" href={telHref()}>
            <span>Programări</span> {business.phone.display}
          </a>
        </div>

        <div className="experience-ghosts" aria-hidden="true">
          {stages.map((stage, index) => (
            <span
              className={`kinetic-title${activeStage === index ? " is-active" : ""}`}
              data-title={stage.title}
              key={stage.id}
            />
          ))}
        </div>

        <EngineBackdrop
          experience={experience}
          stageCount={stages.length}
          onStageChange={setActiveStage}
          onLoadingProgress={setLoadingProgress}
          onReady={handleExperienceReady}
        />

        <div className="experience-overlay">
          {stages.map((stage, index) => {
            const active = activeStage === index;
            return (
              <section key={stage.id} className={`scene-panel scene-${stage.side} scene-${stage.id}${active ? " is-active" : ""}`}>
                {stage.content}
              </section>
            );
          })}
          <div className="experience-meter" aria-hidden="true">
            <i><b style={{ transform: `scaleY(${(activeStage + 1) / stages.length})` }} /></i>
          </div>
          <span className="engine-disclosure">Vizualizare 3D ilustrativă</span>
        </div>

        <div className="experience-scroll" aria-hidden="true">
          {stages.map((stage) => <div className="scroll-stop" id={stage.id} key={stage.id} />)}
        </div>

        <ExperienceLoader progress={loadingProgress} ready={experienceReady} />
      </div>
    </>
  );
}
